import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  buildCatalogueConfiguration,
  selectionFromConfiguration,
} from "@shared-core/domain/catalogue-configuration";
import { resolveCustomValueLadderConfiguration } from "@shared-core/domain/catalogue-custom-value";
import { useCatalogueCustomValueLadder } from "@shared-core/domain/catalogue-custom-value-ladder";
import {
  itemConfigurationContextOf,
  type ItemConfigurationContext,
} from "@shared-core/domain/item-configuration";
import {
  ONE_VERSION,
  VERSIONS_FIELD_KEY,
  quantityStepOf,
  versionCountOf,
  versionsFieldMessages,
  versionsRequestFields,
  type VersionSplitMode,
  type VersionsValue,
} from "@shared-core/domain/line-versions";
import { isItemToChoose } from "./item-to-choose";
import {
  ItemQuantityLadder,
  type ItemPricingState,
} from "../catalogue/ItemQuantityLadder";
import {
  ItemSpecification,
  type ItemSelection,
} from "../catalogue/ItemSpecification";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** The body paths the widget owns: the configuration, its quantity, and how the quantity is shared between versions. */
const CONFIGURATION = "configuration";
const QUANTITY = "quantity";
const VERSION_SPLIT = "version_split";
const VERSION_QUANTITIES = "version_quantities";

/** The version control's value from the body: the count is the configuration's, the rest the two version paths. */
function versionsOf(
  body: Record<string, unknown>,
  typedEntered: (number | null)[] | null,
): VersionsValue {
  const sent = body[CONFIGURATION] as Record<string, string> | undefined;
  const count = versionCountOf(sent?.[VERSIONS_FIELD_KEY]);
  if (count < 2) return ONE_VERSION;
  const split: VersionSplitMode =
    body[VERSION_SPLIT] === "entered" ? "entered" : "even";
  const fromBody = Array.isArray(body[VERSION_QUANTITIES])
    ? (body[VERSION_QUANTITIES] as number[])
    : [];
  return { count, split, entered: typedEntered ?? fromBody };
}

/** The item's price as the lifted ladder reads it: the dry run's, or none. */
function ladderPricing(context: ItemConfigurationContext): ItemPricingState {
  if (context.pricing === "pending") return { status: "loading" };
  return context.price
    ? { status: "ready", preview: context.price, ladderStatus: "ready" }
    : { status: "error" };
}

/**
 * The item configuration widget (One E spec 13 §4.3; flag E7): the catalogue
 * item page's configurator inside the command shell, for Add to draft quote… and Add catalogue item… (which picks its item). It
 * draws the item's specification and its priced quantity ladder from the dry
 * run's `widget_context` (the item page's own controls, lifted), and writes
 * the configuration (the item page's wire format) and the quantity.
 * Everything else the item page offered beside the
 * configuration (the speed, the price option, the price level, the contact,
 * the price itself) is the shell's: the dry run's choices, facts and checks.
 *
 * A custom value is warmed here as on the item page, a rung at a time, each
 * settled rung running the dry run again (`recheck`): the dry run itself never
 * calls anyone. A line no route prices says why, in the dry run's words
 * (order to invoice doc 107 §2a, §2b).
 */
function ItemConfiguration(props: CustomWidgetProps) {
  const { body, errors, setValues, recheck } = props;
  const adapter = useDataAdapter();
  // Mapped once per dry-run answer: the item page's shapes, from the catalogue's.
  const raw = props.hints[CONFIGURATION]?.widget_context;
  const context = useMemo(() => itemConfigurationContextOf(raw), [raw]);
  const item = context?.item ?? null;
  const [selection, setSelection] = useState<ItemSelection | null>(null);
  const [isCustomQuantity, setCustomQuantity] = useState(false);
  const [typedQuantity, setTypedQuantity] = useState<number | null>(null);
  // The boxes of entered version quantities as typed, some still empty; the body holds them once all are filled.
  const [typedEntered, setTypedEntered] = useState<(number | null)[] | null>(
    null,
  );

  // A command that picks its item (Add catalogue item…) can pick another one: the last item's configuration and
  // quantity mean nothing to the new item, so both start again from the dry run's prefill.
  const itemId = item?.id ?? null;
  const lastItemId = useRef<string | null>(null);
  useEffect(() => {
    // No item while the picker is cleared: the body still holds the last item's configuration until another is picked.
    if (itemId === null) return;
    const last = lastItemId.current;
    lastItemId.current = itemId;
    if (last === null || last === itemId) return;
    setSelection(null);
    setCustomQuantity(false);
    setTypedQuantity(null);
    setTypedEntered(null);
    setValues(
      {
        [CONFIGURATION]: undefined,
        [QUANTITY]: undefined,
        [VERSION_SPLIT]: undefined,
        [VERSION_QUANTITIES]: undefined,
      },
      { refill: true },
    );
  }, [itemId, setValues]);

  // The configuration the body holds (the item's default, prefilled by the dry run) opens as its chips.
  const sent = body[CONFIGURATION] as Record<string, string> | undefined;
  useEffect(() => {
    if (item && sent && selection === null)
      setSelection(selectionFromConfiguration(item.fields, sent));
  }, [item, sent, selection]);

  const quantity =
    typedQuantity ?? (typeof body[QUANTITY] === "number" ? body[QUANTITY] : 0);

  const ladderQuantities = useMemo(
    () => (item ? item.quantityBreaks.map((b) => b.qty) : []),
    [item],
  );
  const { configuration: customConfiguration, fieldErrors: customErrors } =
    item && selection
      ? resolveCustomValueLadderConfiguration(item.fields, selection)
      : { configuration: null, fieldErrors: {} };
  const warming = useCatalogueCustomValueLadder({
    itemId: item?.id ?? "",
    configuration: item ? customConfiguration : null,
    quantities: ladderQuantities,
    costFn: (id, request) => adapter.costCatalogueConfiguration(id, request),
    onRungSettled: recheck,
  });

  if (!context || !item || !selection) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {isItemToChoose(props)
          ? "Choose the catalogue item to see its specification and price."
          : "Reading the catalogue item…"}
      </p>
    );
  }

  const versions = versionsOf(body, typedEntered);
  const settled = warming.rungs.filter((r) => r.status === "settled").length;
  const failed = warming.rungs.filter((r) => r.requestError).length;
  const fieldErrors: Record<string, string | undefined> = { ...customErrors };
  for (const error of errors) {
    const field = /^configuration\.(.+)$/.exec(error.field);
    if (field) fieldErrors[field[1]!] = error.message;
  }
  // Why there is no price yet, said at once (not held until a commit is tried): it is what the price panel reads.
  const general = (props.dryRun?.errors ?? errors).filter(
    (e) => e.field === CONFIGURATION,
  );

  const changeVersions = (next: VersionsValue) => {
    setTypedEntered(next.split === "entered" ? next.entered : null);
    const request = versionsRequestFields(next);
    setValues(
      {
        [VERSION_SPLIT]: request.versionSplit,
        [VERSION_QUANTITIES]: request.versionQuantities,
      },
      // Quantities typed into a box wait for the typing to stop; the count and the split are sent at once.
      { debounce: next.split === "entered" && next.count === versions.count },
    );
  };

  const changeSelection = (next: Partial<ItemSelection>) => {
    const merged = { ...selection, ...next };
    setSelection(merged);
    setValues(
      { [CONFIGURATION]: buildCatalogueConfiguration(merged) },
      // A typed custom value or dimension waits for the typing to stop; a chip is sent at once.
      { debounce: "custom" in next },
    );
  };

  // The item's fixed fields are not choices: said once as what it includes, not drawn as rows.
  const fixed = item.fields.filter((f) => f.mode === "fixed");
  const choosable = item.fields.filter((f) => f.mode !== "fixed");

  return (
    <section aria-label="Specification" className="space-y-4">
      {general.length > 0 && (
        <p className="text-sm text-red-600">
          {general.map((e) => e.message).join(" ")}
        </p>
      )}

      {/* What it is first: the choices, then what the item fixes, in one line (change 14). */}
      {fixed.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Includes: {fixed.map((f) => f.fixedValue ?? f.label).join(" · ")}
        </p>
      )}
      {choosable.length > 0 && (
        <div>
          <ItemSpecification
            fields={choosable}
            selection={selection}
            onChange={changeSelection}
            errors={fieldErrors}
            lineVersions={{
              value: versions,
              quantity,
              quantityStep: quantityStepOf(ladderQuantities),
              errors: versionsFieldMessages(errors),
              onChange: changeVersions,
            }}
          />
        </div>
      )}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Quantity</div>
          {warming.warming && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin" />
              Pricing {settled} of {warming.rungs.length} quantities…
            </div>
          )}
        </div>
        {!warming.warming && failed > 0 && (
          <p className="text-xs text-muted-foreground">
            {failed === warming.rungs.length
              ? "Couldn’t reach live pricing for this custom specification. Change the quantity or specification to retry."
              : `Live pricing unavailable for ${failed} of ${warming.rungs.length} quantities — change the quantity or specification to retry.`}
          </p>
        )}
        <ItemQuantityLadder
          quantityBreaks={ladderQuantities}
          selection={{
            quantity,
            customQty: isCustomQuantity,
            priceLevel: null,
            customPricePence: null,
          }}
          onSelect={(next) => {
            setCustomQuantity(next.customQty);
            if (next.customQty) {
              setTypedQuantity(next.quantity);
              // A quantity still being typed (0, empty) is not sent: the body keeps the last one.
              if (next.quantity > 0)
                setValues({ [QUANTITY]: next.quantity }, { debounce: true });
              return;
            }
            setTypedQuantity(null);
            setValues({ [QUANTITY]: next.quantity });
          }}
          pricing={ladderPricing(context)}
        />
      </div>
    </section>
  );
}

export const itemConfigurationWidget: CustomWidget = {
  ownedPaths: [CONFIGURATION, QUANTITY, VERSION_SPLIT, VERSION_QUANTITIES],
  Component: ItemConfiguration,
};

export { ItemConfiguration as ItemConfigurationWidget };
